import React, { useEffect, useState } from "react";
import {
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
} from "@mui/material";
import styled from "styled-components";

const StyledMenuItem = styled(MenuItem)`
    font-size: 12px;
`;

const CustomInputLabel = styled(InputLabel)`
    font-size: 12px;
    &.MuiInputLabel-shrink {
        padding-top: 3px;
    }
`;

const DatabaseSetupSprints = ({
    userDatabases,
    selectedDatabase,
    setSelectedDatabases,
    setAllSprintFields,
    databasesFullPaths,
}) => {
    if (!selectedDatabase.id) return null;

    const [sprintIdField, setSprintIdField] = useState({});
    const [sprintStatusField, setSprintStatusField] = useState({});
    const [sprintTitleField, setSprintTitleField] = useState({});
    const [sprintDatesField, setSprintDatesField] = useState({});

    const databaseSprintFields = [
        {
            id: "notion%3A%2F%2Fsprints%2Funique_id_property",
            name: "Sprint ID",
            acceptedTypes: ["unique_id"],
            adadotName: "id",
            value: sprintIdField,
            set: setSprintIdField,
            databaseId: selectedDatabase.id,
        },
        {
            id: "notion%3A%2F%2Fsprints%2Fsprint_status_property_v2",
            name: "Sprint Status",
            acceptedTypes: ["status"],
            adadotName: "status",
            value: sprintStatusField,
            set: setSprintStatusField,
            databaseId: selectedDatabase.id,
        },
        {
            id: "title",
            name: "Sprint Name",
            acceptedTypes: ["title", "text"],
            adadotName: "title",
            value: sprintTitleField,
            set: setSprintTitleField,
            databaseId: selectedDatabase.id,
        },
        {
            id: "notion%3A%2F%2Fsprints%2Fdates_property",
            name: "Dates",
            acceptedTypes: ["date"],
            adadotName: "date_range",
            value: sprintDatesField,
            set: setSprintDatesField,
            databaseId: selectedDatabase.id,
        },
    ];

    React.useEffect(() => {
        setAllSprintFields((prevFields) => {
            return [
                ...prevFields.map((prevField) => {
                    const editedField = databaseSprintFields.find(
                        (newField) =>
                            newField.adadotName === prevField.adadotName &&
                            newField.id === prevField.id &&
                            newField.databaseId === prevField.databaseId
                    );
                    if (editedField) return editedField;
                    return prevField;
                }),
                ...databaseSprintFields.filter(
                    (newField) =>
                        !prevFields.find(
                            (prevField) =>
                                newField.adadotName === prevField.adadotName &&
                                newField.id === prevField.id &&
                                newField.databaseId === prevField.databaseId
                        )
                ),
            ];
        });
    }, [sprintDatesField, sprintTitleField, sprintStatusField, sprintIdField, setAllSprintFields]);

    useEffect(() => {
        if (selectedDatabase && selectedDatabase.properties) {
            Object.values(selectedDatabase.properties).forEach((property) => {
                let matchedField = databaseSprintFields.find((field) => field.id === property.id);
                if (matchedField) {
                    matchedField.set(property);
                } else {
                    matchedField = databaseSprintFields.find(
                        (field) =>
                            field.name.toLowerCase() === property.name.toLowerCase() &&
                            field.acceptedTypes.includes(property.type)
                    );
                }
                if (matchedField) {
                    matchedField.set(property);
                }
            });
        }
    }, [selectedDatabase]);

    useEffect(() => {
        if (selectedDatabase && selectedDatabase.properties) {
            Object.values(selectedDatabase.properties).forEach((property) => {
                let matchedField = databaseSprintFields.find((field) => field.id === property.id);
                if (matchedField) {
                    matchedField.set(property);
                } else {
                    matchedField = databaseSprintFields.find(
                        (field) =>
                            field.name.toLowerCase() === property.name.toLowerCase() &&
                            field.acceptedTypes.includes(property.type)
                    );
                }
                if (matchedField) {
                    matchedField.set(property);
                }
            });
        }
    }, [selectedDatabase]);

    const handleDatabaseChange = (e) => {
        const selected = userDatabases.find((db) => db.id === e.target.value);
        setSelectedDatabases(selected);
    };

    return (
        <Grid item style={{ marginTop: "40px" }}>
            <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                <FormControl
                    variant="outlined"
                    margin="1"
                    size="small"
                    error={!selectedDatabase}
                    fullWidth
                >
                    <CustomInputLabel sx={{ fontSize: 12, paddingTop: "3px" }} id="database-select">
                        Database
                    </CustomInputLabel>
                    <Select
                        labelId="database-select"
                        label="Database"
                        value={selectedDatabase.id || ""}
                        sx={{ fontSize: 12 }}
                        onChange={handleDatabaseChange}
                    >
                        {userDatabases
                            .slice()
                            .sort((a, b) => {
                                const titleA =
                                    a.title[0]?.plain_text?.toLowerCase() || "untitled database";
                                const titleB =
                                    b.title[0]?.plain_text?.toLowerCase() || "untitled database";
                                return titleA.localeCompare(titleB);
                            })
                            .map((db) => (
                                <StyledMenuItem
                                    value={db.id}
                                    key={db.id}
                                    selected={db.id === selectedDatabase.id}
                                >
                                    {db.title[0]?.plain_text || "Untitled Database"}
                                    {db.title[0]?.plain_text && databasesFullPaths[db.id]
                                        ? ` - ${databasesFullPaths[db.id]}`
                                        : null}
                                </StyledMenuItem>
                            ))}
                    </Select>
                    {!selectedDatabase && <FormHelperText>Please select a database</FormHelperText>}
                </FormControl>
            </Stack>
            {selectedDatabase && selectedDatabase.properties && (
                <Grid
                    container
                    spacing={3}
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    style={{ marginTop: "10px" }}
                >
                    {databaseSprintFields.map((field) => {
                        return (
                            <Grid item key={field.name} width="100%">
                                <FormControl variant="outlined" margin="1" size="small" fullWidth>
                                    <CustomInputLabel
                                        sx={{ fontSize: 12 }}
                                        id={`${field.name}-select`}
                                    >
                                        {field.name}
                                    </CustomInputLabel>
                                    <Select
                                        labelId={`${field.name}-select`}
                                        label={field.name}
                                        value={field.value.id || ""}
                                        sx={{ fontSize: 12 }}
                                        onChange={(e) => {
                                            const selectedProp = Object.values(
                                                selectedDatabase.properties
                                            ).find((property) => property.id === e.target.value);
                                            field.set(selectedProp);
                                        }}
                                    >
                                        {Object.values(selectedDatabase.properties).map(
                                            (property) => (
                                                <StyledMenuItem
                                                    value={property.id}
                                                    key={property.id}
                                                    selected={property.id === field.value.id}
                                                    disabled={
                                                        !field.acceptedTypes.includes(property.type)
                                                    }
                                                >
                                                    <span>{property.name}</span>
                                                </StyledMenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Grid>
    );
};

export default DatabaseSetupSprints;
